import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Download, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/Button';
import { cn } from '../ui/utils';
import type { CaseDocument } from '../../types';
import { REVIEW_DECISION_CONFIG } from '../../types';
import {
  EXPORT_DECISIONS,
  defaultExportSelection,
  exportCounts,
  type ExportDecision,
  type ExportFormat,
  type ExportSelection,
} from './exportSelection';

interface ExportCaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documents: CaseDocument[];
  onExport: (format: ExportFormat, selection: ExportSelection) => void;
  isExporting: boolean;
}

const FORMAT_OPTIONS: { value: ExportFormat; label: string }[] = [
  { value: 'zip', label: 'ZIP of files' },
  { value: 'pdf', label: 'Single PDF' },
];

const DECISION_LABELS: Record<ExportDecision, string> = {
  responsive: REVIEW_DECISION_CONFIG.responsive.label,
  needs_review: REVIEW_DECISION_CONFIG.needs_review.label,
  non_responsive: REVIEW_DECISION_CONFIG.non_responsive.label,
  undecided: 'No decision yet',
};

const NOT_FINALIZED_NAMES_SHOWN = 5;

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

/**
 * Export the case as a ZIP or a single PDF, choosing which documents go in by
 * review decision and by whether their redaction was finalized (sc-10997).
 */
export function ExportCaseDialog({ open, onOpenChange, documents, onExport, isExporting }: ExportCaseDialogProps) {
  const [format, setFormat] = useState<ExportFormat>('zip');
  const [selection, setSelection] = useState<ExportSelection>(() => defaultExportSelection(documents));
  const [showNotFinalized, setShowNotFinalized] = useState(false);

  // Each time the dialog opens, start from the defaults for the case as it is now.
  useEffect(() => {
    if (!open) return;
    setSelection(defaultExportSelection(documents));
    setShowNotFinalized(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on open only
  }, [open]);

  const counts = useMemo(() => exportCounts(documents, format, selection), [documents, format, selection]);
  const notFinalizedCount = counts.notFinalized.length;

  const toggleDecision = (decision: ExportDecision, checked: boolean) => {
    setSelection((prev) => ({
      ...prev,
      decisions: checked
        ? EXPORT_DECISIONS.filter((d) => d === decision || prev.decisions.includes(d))
        : prev.decisions.filter((d) => d !== decision),
    }));
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !isExporting && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export case</DialogTitle>
          <DialogDescription>Choose the format and which documents go in.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">Format</div>
            <div
              role="radiogroup"
              aria-label="Export format"
              className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50"
            >
              {FORMAT_OPTIONS.map((option) => {
                const selected = format === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={isExporting}
                    onClick={() => setFormat(option.value)}
                    className={cn(
                      'px-4 py-1.5 text-sm font-medium rounded-md transition-colors disabled:opacity-50',
                      selected ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <fieldset className="space-y-1.5">
            <legend className="mb-2 text-sm font-medium text-gray-700">Documents</legend>
            {EXPORT_DECISIONS.map((decision) => (
              <label
                key={decision}
                className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  aria-label={DECISION_LABELS[decision]}
                  checked={selection.decisions.includes(decision)}
                  disabled={isExporting}
                  onChange={(e) => toggleDecision(decision, e.target.checked)}
                />
                <span className="flex-1">{DECISION_LABELS[decision]}</span>
                <span className="tabular-nums text-gray-500">{counts.byDecision[decision]}</span>
              </label>
            ))}
          </fieldset>

          <fieldset className="space-y-1.5">
            <legend className="mb-2 text-sm font-medium text-gray-700">Redaction</legend>
            {format === 'zip' ? (
              <>
                <p className="text-sm text-gray-500">
                  A ZIP holds redacted files, so only finalized documents go in.
                </p>
                <label className="flex items-start gap-2 pt-1 text-sm text-gray-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={selection.keepOriginalFormat}
                    disabled={isExporting}
                    onChange={(e) => setSelection((prev) => ({ ...prev, keepOriginalFormat: e.target.checked }))}
                    aria-describedby="export-keep-original-note"
                  />
                  <span>
                    Files with no redactions: keep the original format
                    <span id="export-keep-original-note" className="block text-xs text-gray-500">
                      Original files keep their metadata and hidden content (comments, tracked changes, hidden
                      sheets), which the PDF copy drops. Emails always go in as their redacted copy.
                    </span>
                  </span>
                </label>
              </>
            ) : (
              <>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                  <input
                    type="radio"
                    name="export-redaction"
                    checked={selection.finalizedOnly}
                    disabled={isExporting}
                    onChange={() => setSelection((prev) => ({ ...prev, finalizedOnly: true }))}
                  />
                  Finalized only
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                  <input
                    type="radio"
                    name="export-redaction"
                    checked={!selection.finalizedOnly}
                    disabled={isExporting}
                    onChange={() => setSelection((prev) => ({ ...prev, finalizedOnly: false }))}
                  />
                  Include documents that aren't finalized
                </label>
                {!selection.finalizedOnly && (
                  <p className="flex items-start gap-1.5 text-xs text-amber-700">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    Documents that aren't finalized go in unredacted, marked on their separator page.
                  </p>
                )}
              </>
            )}
          </fieldset>

          <div className="rounded-md bg-gray-50 px-3 py-2 text-sm" aria-live="polite">
            <div className="font-medium text-gray-900">
              {counts.included > 0 ? `${plural(counts.included, 'document')} will be exported` : 'No documents match'}
            </div>
            {notFinalizedCount > 0 && (
              <div className="mt-1 text-gray-600">
                {plural(notFinalizedCount, 'selected document')}{' '}
                {format === 'zip'
                  ? `${notFinalizedCount === 1 ? 'has' : 'have'} no finalized redacted file`
                  : `${notFinalizedCount === 1 ? "isn't" : "aren't"} finalized`}{' '}
                and {notFinalizedCount === 1 ? 'is' : 'are'} left out.{' '}
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={() => setShowNotFinalized((v) => !v)}
                >
                  {showNotFinalized ? 'Hide' : 'Show'}
                </button>
                {showNotFinalized && (
                  <ul className="mt-1 list-disc pl-5 text-xs text-gray-500">
                    {counts.notFinalized.slice(0, NOT_FINALIZED_NAMES_SHOWN).map((doc) => (
                      <li key={doc.id} className="truncate">{doc.name}</li>
                    ))}
                    {notFinalizedCount > NOT_FINALIZED_NAMES_SHOWN && (
                      <li>and {notFinalizedCount - NOT_FINALIZED_NAMES_SHOWN} more</li>
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isExporting}>
            Cancel
          </Button>
          <Button onClick={() => onExport(format, selection)} disabled={isExporting || counts.included === 0}>
            {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {isExporting ? 'Exporting…' : 'Export'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
